import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import { 
  FiMail, FiMessageCircle, FiPhone, FiCheck, FiSettings, 
  FiClock, FiSliders, FiFileText, FiActivity, FiServer 
} from "react-icons/fi";

const NotificationSettings = () => {
  const [loading, setLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [settings, setSettings] = useState({
    smsEnabled: false,
    emailEnabled: false,
    whatsappEnabled: false,
    autoAbsentAlert: true,
    absentAlertMode: "DAILY_CONSOLIDATED",
    autoResultAlert: true,
    smsGatewayUrl: "",
    smsApiKey: "",
    twilioSid: "",
    twilioToken: "",
    twilioFromNumber: "",
    whatsappToken: "",
    whatsappPhoneNumberId: "",
    smtpHost: "",
    smtpPort: 587,
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
    absentInstantTemplate: "",
    absentConsolidatedTemplate: "",
    resultsTemplate: ""
  });

  const [activeTab, setActiveTab] = useState("settings"); // "settings" or "logs"

  const [testEmailAddr, setTestEmailAddr] = useState("");
  const [testPhoneNum, setTestPhoneNum] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingSMS, setTestingSMS] = useState(false);

  // Fetch settings from API
  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseApiURL()}/notification/settings`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (response.data.success) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  // Dispatch Test Email
  const handleTestEmail = async () => {
    if (!testEmailAddr.trim()) {
      toast.error("Please enter a recipient email address.");
      return;
    }
    setTestingEmail(true);
    const testToast = toast.loading("Sending test email...");
    try {
      const res = await axios.post(`${baseApiURL()}/notification/test-email`, { targetEmail: testEmailAddr.trim() }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      toast.dismiss(testToast);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || "Failed to send test email");
      }
    } catch (err) {
      toast.dismiss(testToast);
      toast.error(err.response?.data?.message || err.message || "Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  // Dispatch Test SMS
  const handleTestSMS = async () => {
    if (!testPhoneNum.trim()) {
      toast.error("Please enter a recipient phone number.");
      return;
    }
    setTestingSMS(true);
    const testToast = toast.loading("Sending test SMS...");
    try {
      const res = await axios.post(`${baseApiURL()}/notification/test-sms`, { targetPhone: testPhoneNum.trim() }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      toast.dismiss(testToast);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message || "Failed to send test SMS");
      }
    } catch (err) {
      toast.dismiss(testToast);
      toast.error(err.response?.data?.message || err.message || "Failed to send test SMS");
    } finally {
      setTestingSMS(false);
    }
  };

  // Fetch logs from API
  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const response = await axios.get(`${baseApiURL()}/notification/logs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`
        }
      });
      if (response.data.success) {
        setLogs(response.data.logs);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch notification logs");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs();
    }
  }, [activeTab]);

  // Handle setting updates locally
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  // Handle custom dropdowns or toggles
  const setDirectValue = (name, value) => {
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Save settings to DB
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    const saveToast = toast.loading("Saving configuration...");
    try {
      const response = await axios.post(
        `${baseApiURL()}/notification/settings/update`,
        settings,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`
          }
        }
      );
      toast.dismiss(saveToast);
      if (response.data.success) {
        toast.success("Settings updated successfully!");
        setSettings(response.data.settings);
      } else {
        toast.error("Failed to update settings");
      }
    } catch (error) {
      toast.dismiss(saveToast);
      console.error(error);
      toast.error(error.response?.data?.error || "Error saving configuration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Notification Console</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Configure automated alerts, communication gateways, templates, and track message logs</p>
        </div>
      </div>
        
      {/* Navigation Tabs */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setActiveTab("settings")}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2 ${
              activeTab === "settings"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiSettings /> Configuration
          </button>
          <button
            onClick={() => setActiveTab("logs")}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer flex items-center justify-center gap-2 ${
              activeTab === "logs"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
          >
            <FiActivity /> Dispatch Logs
          </button>
        </div>
      </div>

      {activeTab === "settings" ? (
        <form onSubmit={handleSave} className="space-y-6">
          {/* Card 1: Channel Status Toggles */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs">
            <h2 className="text-sm font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4">
              <FiSliders /> Active Communication Channels
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* SMS Toggle */}
              <label className={`relative flex flex-col justify-between p-5 rounded-xl border-2 cursor-pointer transition ${
                settings.smsEnabled 
                  ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20" 
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl text-xl">
                    <FiPhone />
                  </div>
                  <input
                    type="checkbox"
                    name="smsEnabled"
                    checked={settings.smsEnabled}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                    settings.smsEnabled ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 dark:border-slate-700"
                  }`}>
                    {settings.smsEnabled && <FiCheck size={12} />}
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">SMS Alerts</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    Send updates to parent phone numbers via Twilio or an API Gateway.
                  </p>
                </div>
              </label>

              {/* WhatsApp Toggle */}
              <label className={`relative flex flex-col justify-between p-5 rounded-xl border-2 cursor-pointer transition ${
                settings.whatsappEnabled 
                  ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20" 
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-xl text-xl">
                    <FiMessageCircle />
                  </div>
                  <input
                    type="checkbox"
                    name="whatsappEnabled"
                    checked={settings.whatsappEnabled}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                    settings.whatsappEnabled ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-300 dark:border-slate-700"
                  }`}>
                    {settings.whatsappEnabled && <FiCheck size={12} />}
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">WhatsApp Portal</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    Send automated chat details using official Meta Business Cloud API.
                  </p>
                </div>
              </label>

              {/* Email Toggle */}
              <label className={`relative flex flex-col justify-between p-5 rounded-xl border-2 cursor-pointer transition ${
                settings.emailEnabled 
                  ? "border-purple-500 bg-purple-50/50 dark:bg-purple-950/20" 
                  : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-slate-50/50 dark:bg-slate-950/40"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="p-2.5 bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 rounded-xl text-xl">
                    <FiMail />
                  </div>
                  <input
                    type="checkbox"
                    name="emailEnabled"
                    checked={settings.emailEnabled}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center border-2 ${
                    settings.emailEnabled ? "border-purple-600 bg-purple-600 text-white" : "border-slate-300 dark:border-slate-700"
                  }`}>
                    {settings.emailEnabled && <FiCheck size={12} />}
                  </div>
                </div>
                <div className="mt-4">
                  <h3 className="font-bold text-sm text-slate-900 dark:text-white">Email Reports</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                    Send HTML report cards and full statements using Nodemailer SMTP.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Card 2: System Trigger Logic */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs">
            <h2 className="text-sm font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-4">
              <FiClock /> Automation Schedules & Triggers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Attendance triggers */}
              <div className="space-y-3 bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-xs text-slate-900 dark:text-white uppercase tracking-wider">Absence Alerts</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Trigger warnings when a student is absent.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="autoAbsentAlert"
                      checked={settings.autoAbsentAlert}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                {settings.autoAbsentAlert && (
                  <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-2.5">
                    <label className="block text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">Alert Delivery Strategy</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setDirectValue("absentAlertMode", "INSTANT")}
                        className={`py-2 px-3 rounded-xl text-xs font-bold border transition cursor-pointer ${
                          settings.absentAlertMode === "INSTANT"
                            ? "bg-indigo-600 border-indigo-500 text-white shadow-xs"
                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        Instant (Per Period)
                      </button>
                      <button
                        type="button"
                        onClick={() => setDirectValue("absentAlertMode", "DAILY_CONSOLIDATED")}
                        className={`py-2 px-3 rounded-xl text-xs font-bold border transition cursor-pointer ${
                          settings.absentAlertMode === "DAILY_CONSOLIDATED"
                            ? "bg-indigo-600 border-indigo-500 text-white shadow-xs"
                            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        Consolidated (Daily 5 PM)
                      </button>
                    </div>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                      {settings.absentAlertMode === "DAILY_CONSOLIDATED" 
                        ? "✓ Recommended. Collects all absentees and sends one daily message to parents."
                        : "⚠ Sends separate SMS for every absent period immediately when class is marked."}
                    </p>
                  </div>
                )}
              </div>

              {/* Result Triggers */}
              <div className="space-y-3 bg-slate-50 dark:bg-slate-950 p-5 rounded-xl border border-slate-200 dark:border-slate-800 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-xs text-slate-900 dark:text-white uppercase tracking-wider">Results Publishing Alerts</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Send notifications when midterm or external marks are uploaded.</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="autoResultAlert"
                      checked={settings.autoResultAlert}
                      onChange={handleChange}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 pt-3 border-t border-slate-200 dark:border-slate-800">
                  Runs instantly in the background as soon as faculty saves student marks.
                </div>
              </div>
            </div>
          </div>

          {/* Card 3: Gateway Credentials */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs space-y-6">
            <h2 className="text-sm font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
              <FiServer /> Gateway Credentials
            </h2>

            {/* Twilio SMS Setup */}
            {settings.smsEnabled && (
              <div className="space-y-3 p-5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                <h3 className="font-bold text-xs text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                  <FiPhone /> Twilio SMS Provider Config
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Account SID</label>
                    <input
                      type="text"
                      name="twilioSid"
                      value={settings.twilioSid}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Auth Token</label>
                    <input
                      type="password"
                      name="twilioToken"
                      value={settings.twilioToken}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="••••••••••••••••••••••••••••••••"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Twilio Phone / Sender ID</label>
                    <input
                      type="text"
                      name="twilioFromNumber"
                      value={settings.twilioFromNumber}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="+1234567890"
                    />
                  </div>
                </div>
                
                {/* Fallback API URL */}
                <div className="pt-2 border-t border-slate-200 dark:border-slate-800">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Alternative Custom SMS HTTP URL Gateway (Optional)</label>
                  <input
                    type="text"
                    name="smsGatewayUrl"
                    value={settings.smsGatewayUrl}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="https://api.smshorizon.in/api/v2/sendsms.php?apikey={apikey}&to={to}&message={message}"
                  />
                  <small className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 block">
                    Use placeholders: <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded text-indigo-600 dark:text-indigo-400">{`{to}`}</code> for student phone and <code className="bg-slate-200 dark:bg-slate-800 px-1 py-0.5 rounded text-indigo-600 dark:text-indigo-400">{`{message}`}</code> for templates.
                  </small>
                </div>

                {/* Test SMS Widget */}
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center gap-2.5">
                  <input
                    type="text"
                    value={testPhoneNum}
                    onChange={(e) => setTestPhoneNum(e.target.value)}
                    placeholder="Enter phone number to test (e.g. +919876543210)"
                    className="flex-1 w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleTestSMS}
                    disabled={testingSMS}
                    className="w-full md:w-auto px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs shadow-xs transition shrink-0 cursor-pointer"
                  >
                    {testingSMS ? "Testing SMS..." : "Test SMS Connection"}
                  </button>
                </div>
              </div>
            )}

            {/* Meta WhatsApp Setup */}
            {settings.whatsappEnabled && (
              <div className="space-y-3 p-5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                <h3 className="font-bold text-xs text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                  <FiMessageCircle /> Meta WhatsApp Business API Config
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">Phone Number ID</label>
                    <input
                      type="text"
                      name="whatsappPhoneNumberId"
                      value={settings.whatsappPhoneNumberId}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="109283749827364"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">System User Access Token</label>
                    <input
                      type="password"
                      name="whatsappToken"
                      value={settings.whatsappToken}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="EAAGxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Nodemailer SMTP Config */}
            {settings.emailEnabled && (
              <div className="space-y-3 p-5 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                <h3 className="font-bold text-xs text-purple-600 dark:text-purple-400 uppercase tracking-wider flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
                  <FiMail /> Nodemailer SMTP Mail Gateway Config
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">SMTP Host Server</label>
                    <input
                      type="text"
                      name="smtpHost"
                      value={settings.smtpHost}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">SMTP Port</label>
                    <input
                      type="number"
                      name="smtpPort"
                      value={settings.smtpPort}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="587"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">From Sender Address</label>
                    <input
                      type="email"
                      name="smtpFrom"
                      value={settings.smtpFrom}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="no-reply@college.edu"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">SMTP User / Username</label>
                    <input
                      type="text"
                      name="smtpUser"
                      value={settings.smtpUser}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="college.portal@gmail.com"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">SMTP Password / Google App Password</label>
                    <input
                      type="password"
                      name="smtpPass"
                      value={settings.smtpPass}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="••••••••••••••••"
                    />
                  </div>
                </div>

                {/* Test Email Widget */}
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-col md:flex-row items-center gap-2.5">
                  <input
                    type="email"
                    value={testEmailAddr}
                    onChange={(e) => setTestEmailAddr(e.target.value)}
                    placeholder="Enter email address to test (e.g. yourname@gmail.com)"
                    className="flex-1 w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs text-slate-900 dark:text-white font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleTestEmail}
                    disabled={testingEmail}
                    className="w-full md:w-auto px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold text-xs shadow-xs transition shrink-0 cursor-pointer"
                  >
                    {testingEmail ? "Testing Mail..." : "Test Mail Connection"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Card 4: Customize Message Templates */}
          <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs space-y-4">
            <h2 className="text-sm font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
              <FiFileText /> Custom Message Templates
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Manage what alerts parents see. You can inject placeholders: 
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{student_name}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{period}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{subject}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{date}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{absent_count}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{exam_type}`}</code>,
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{marks_summary}`}</code>, and 
              <code className="text-indigo-600 dark:text-indigo-400 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded mx-1">{`{portal_link}`}</code>.
            </p>

            <div className="space-y-4">
              {/* Template 1 */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Absent (Instant Alert) Text</label>
                <textarea
                  name="absentInstantTemplate"
                  value={settings.absentInstantTemplate}
                  onChange={handleChange}
                  rows="3"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              {/* Template 2 */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Absent (Daily Consolidated Alert) Text</label>
                <textarea
                  name="absentConsolidatedTemplate"
                  value={settings.absentConsolidatedTemplate}
                  onChange={handleChange}
                  rows="3"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>

              {/* Template 3 */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Results Publishing Text</label>
                <textarea
                  name="resultsTemplate"
                  value={settings.resultsTemplate}
                  onChange={handleChange}
                  rows="3"
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl px-3.5 py-2.5 text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={fetchSettings}
              className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-bold transition cursor-pointer"
            >
              Discard Changes
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold shadow-xs disabled:opacity-50 transition cursor-pointer"
            >
              {loading ? "Saving..." : "Save Settings"}
            </button>
          </div>
        </form>
      ) : (
        /* Logs Section */
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 shadow-xs">
          <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 mb-4">
            <h2 className="text-sm font-bold flex items-center gap-2 text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
              <FiActivity /> Active Dispatch History
            </h2>
            <button
              onClick={fetchLogs}
              className="text-xs px-3.5 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl font-bold transition cursor-pointer"
            >
              Refresh Table
            </button>
          </div>

          {logsLoading ? (
            <div className="flex justify-center items-center py-16 text-slate-500 dark:text-slate-400 text-xs font-medium">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
              <span>Reading logs from DB...</span>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16 text-slate-400 dark:text-slate-500 text-xs font-medium">
              No alert logs registered in the database yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                    <th className="pb-3 pr-4">Student</th>
                    <th className="pb-3 px-4">Channel</th>
                    <th className="pb-3 px-4">Type</th>
                    <th className="pb-3 px-4">Recipient</th>
                    <th className="pb-3 px-4">Sent Time</th>
                    <th className="pb-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                  {logs.map((log) => (
                    <tr key={log._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-3.5 pr-4">
                        <div className="font-bold text-slate-900 dark:text-white">{log.recipientName}</div>
                        <div className="text-[11px] font-mono text-slate-400 dark:text-slate-500">{log.enrollmentNo}</div>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-xs">
                        <span className={`px-2.5 py-0.5 rounded-lg text-[10px] font-bold ${
                          log.channel === "SMS" ? "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800" :
                          log.channel === "WHATSAPP" ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-800" :
                          "bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800"
                        }`}>
                          {log.channel}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-xs font-semibold">
                        {log.type}
                      </td>
                      <td className="py-3.5 px-4 text-xs font-mono text-slate-600 dark:text-slate-400">
                        {log.recipientContact}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-slate-400 dark:text-slate-500">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="py-3.5 px-4 text-xs">
                        {log.status === "SENT" ? (
                          <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-bold">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                            Delivered
                          </span>
                        ) : log.status === "FAILED" ? (
                          <div>
                            <span className="text-rose-600 dark:text-rose-400 font-bold block">Failed</span>
                            <span className="text-[10px] text-slate-400 max-w-xs block truncate">{log.errorMessage}</span>
                          </div>
                        ) : (
                          <span className="text-amber-600 dark:text-amber-400 font-bold">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
